/**
 * CalculatorChooserPage — intermediate screen between the Meeting Mode
 * lead-picker and the actual calculator. Lists every calculator enabled for
 * the current tenant; rep picks one and we record an audit row before
 * navigating.
 *
 * Track D-CHOOSER (May 2026) — refactor of the Meeting Mode entry flow.
 * Before: Lead picker → Pool Calculator (auto). After: Lead picker →
 * Calculator List → Pool Calculator (or future SWH / AC tonnage / etc.).
 *
 * The page consumes the meeting session set by MeetingModeLauncher
 * (sessionStorage key `pool-calc.meeting-session`). If missing/expired we
 * bounce back to the launcher.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator,
  Loader2,
  ArrowRight,
  AlertTriangle,
  Droplet,
  Sun,
  Wind,
  Square,
  ThermometerSun,
} from 'lucide-react';
import { api } from '@/utils/api';
import { readStoredSession } from './MeetingModeLauncher';

interface CalculatorDescriptor {
  key: string;
  title: string;
  subtitle: string;
  iconHint: string;
  module: string;
  openRoute: string;
  mobileRoute: string;
}

function iconFor(hint: string) {
  switch (hint) {
    case 'pool':
      return <Droplet size={22} />;
    case 'solar':
      return <Sun size={22} />;
    case 'ac':
      return <Wind size={22} />;
    // Bug B-6 (May 2026, QA-FIX): Commercial Heat Pump card was rendering
    // an empty Square placeholder because the registry's iconHint='heat-pump'
    // had no mapping here. ThermometerSun matches the hot-water-heating
    // semantics of the Commercial Heat Pump (Hot Water) calculator.
    case 'heat-pump':
      return <ThermometerSun size={22} />;
    default:
      return <Square size={22} />;
  }
}

export function CalculatorChooserPage() {
  const navigate = useNavigate();
  const [calcs, setCalcs] = useState<CalculatorDescriptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  const session = useMemo(() => readStoredSession(), []);
  const leadId = session?.leadId ?? null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = leadId ? `?leadId=${encodeURIComponent(leadId)}` : '';
        const res = await api.get<{ success: boolean; data: CalculatorDescriptor[] }>(
          `/calculators${params}`
        );
        if (!cancelled) {
          // Defensive — backend should always wrap in {success,data}.
          const rows = Array.isArray((res.data as any)?.data)
            ? ((res.data as any).data as CalculatorDescriptor[])
            : [];
          setCalcs(rows);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.error || 'Could not load calculators.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  async function openCalc(calc: CalculatorDescriptor) {
    if (opening) return;
    setOpening(calc.key);
    setError(null);
    try {
      // Audit log first — don't block navigation if the audit POST fails;
      // log to console and proceed (the user shouldn't be punished for an
      // observability glitch).
      try {
        await api.post('/calculators/opens', {
          calculatorKey: calc.key,
          leadId: leadId ?? null,
          // meetingSessionId is the row id, not the JWT — we only have the JWT
          // client-side, so we leave this null. Sessionless audit rows still
          // join to lead/user/tenant, which is enough for coaching analytics.
          meetingSessionId: null,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[calc-chooser] audit POST failed (non-fatal):', err);
      }

      // Navigate. The current openRoute for the pool calculator is
      // `/sales/meeting/calculator?session={sessionToken}` — we strip the
      // template since the session is already in sessionStorage and the
      // existing PoolCalculatorPage reads it directly. Future calculators may
      // use the placeholder if they want a URL-bound token.
      const target = calc.openRoute.replace('?session={sessionToken}', '');
      navigate(target);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not open calculator.');
    } finally {
      setOpening(null);
    }
  }

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Calculator size={28} style={{ color: '#0066b3' }} />
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Choose a calculator</h1>
      </div>
      <p style={{ color: '#666', marginTop: 4 }}>
        {session?.leadLabel
          ? `Meeting Mode · bound to ${session.leadLabel}.`
          : 'Meeting Mode · sizing without a lead. The quotation can be re-attached later.'}
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Loader2 className="animate-spin" />
        </div>
      ) : error ? (
        <div
          style={{
            padding: 12,
            background: '#fde7e7',
            color: '#8b2222',
            borderRadius: 6,
            marginTop: 12,
            display: 'flex',
            gap: 8,
          }}
        >
          <AlertTriangle size={16} />
          {error}
        </div>
      ) : calcs.length === 0 ? (
        <div
          style={{
            marginTop: 24,
            padding: 32,
            textAlign: 'center',
            color: '#666',
            border: '1px dashed #ddd',
            borderRadius: 6,
            fontSize: 14,
          }}
        >
          No calculators are enabled. Talk to your AURA admin.
        </div>
      ) : (
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {calcs.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => openCalc(c)}
              disabled={!!opening}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '14px 16px',
                background: 'white',
                border: '1px solid #e5e5e5',
                borderRadius: 8,
                cursor: opening ? 'wait' : 'pointer',
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                opacity: opening && opening !== c.key ? 0.5 : 1,
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!opening) e.currentTarget.style.borderColor = '#0066b3';
              }}
              onMouseLeave={(e) => {
                if (!opening) e.currentTarget.style.borderColor = '#e5e5e5';
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: '#f3f8ff',
                  color: '#0066b3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {iconFor(c.iconHint)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{c.title}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{c.subtitle}</div>
              </div>
              {opening === c.key ? (
                <Loader2 size={18} className="animate-spin" style={{ color: '#0066b3' }} />
              ) : (
                <ArrowRight size={18} style={{ color: '#999' }} />
              )}
            </button>
          ))}
        </div>
      )}

      <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-start' }}>
        <button
          type="button"
          onClick={() => navigate('/sales/meeting/start')}
          style={{
            padding: '8px 16px',
            border: '1px solid #ccc',
            background: 'white',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          ← Back to lead picker
        </button>
      </div>
    </div>
  );
}
